/**
 * Chunk Error Handler
 * Handles dynamic import failures and stale chunk errors in SPAs
 * Automatically refreshes the page when chunks are missing due to deployments
 */

interface ChunkErrorHandlerOptions {
  maxRetries?: number;
  retryDelay?: number;
  enableLogging?: boolean;
  onError?: (error: Error, retryCount: number) => void;
  onMaxRetriesReached?: (error: Error) => void;
}

class ChunkErrorHandler {
  private retryCount = 0;
  private maxRetries: number;
  private retryDelay: number;
  private enableLogging: boolean;
  private onError?: (error: Error, retryCount: number) => void;
  private onMaxRetriesReached?: (error: Error) => void;

  constructor(options: ChunkErrorHandlerOptions = {}) {
    this.maxRetries = options.maxRetries ?? 3;
    this.retryDelay = options.retryDelay ?? 1000;
    this.enableLogging = options.enableLogging ?? true;
    this.onError = options.onError;
    this.onMaxRetriesReached = options.onMaxRetriesReached;
  }

  /**
   * Check if error is a chunk loading error
   */
  private isChunkError(error: Error): boolean {
    const chunkErrorPatterns = [
      /Loading chunk \d+ failed/,
      /Failed to fetch dynamically imported module/,
      /Loading CSS chunk \d+ failed/,
      /ChunkLoadError/,
      /NetworkError/,
      /Failed to import/,
      /Script error/
    ];

    return chunkErrorPatterns.some(pattern => 
      pattern.test(error.message) || pattern.test(error.name)
    );
  }

  /**
   * Check if error is related to authentication
   */
  private isAuthError(error: Error): boolean {
    const authErrorPatterns = [
      /auth\/id-token-expired/,
      /auth\/user-token-expired/,
      /auth\/invalid-user-token/,
      /auth\/requires-recent-login/,
      /Unauthorized/,
      /401/
    ];

    return authErrorPatterns.some(pattern => 
      pattern.test(error.message) || pattern.test(error.name)
    );
  }

  /**
   * Handle chunk loading errors
   */
  async handleChunkError(error: Error): Promise<void> {
    if (!this.isChunkError(error)) {
      throw error; // Re-throw if not a chunk error
    }

    this.retryCount++;

    if (this.enableLogging) {
      console.warn(`Chunk loading error (attempt ${this.retryCount}/${this.maxRetries}):`, {
        error: error.message,
        stack: error.stack,
        url: window.location.href,
        userAgent: navigator.userAgent,
        timestamp: new Date().toISOString()
      });
    }

    // Call error callback if provided
    if (this.onError) {
      this.onError(error, this.retryCount);
    }

    if (this.retryCount >= this.maxRetries) {
      if (this.enableLogging) {
        console.error('Max retries reached for chunk loading. Forcing page reload.');
      }

      if (this.onMaxRetriesReached) {
        this.onMaxRetriesReached(error);
      }

      // Force a hard reload to get fresh chunks
      window.location.reload();
      return;
    }

    // Wait before retrying
    await new Promise(resolve => setTimeout(resolve, this.retryDelay));

    // Try to reload the page
    window.location.reload();
  }

  /**
   * Handle authentication errors
   */
  async handleAuthError(error: Error): Promise<void> {
    if (!this.isAuthError(error)) {
      throw error; // Re-throw if not an auth error
    }

    if (this.enableLogging) {
      console.warn('Authentication error detected:', {
        error: error.message,
        url: window.location.href,
        timestamp: new Date().toISOString()
      });
    }

    // Clear any cached auth data
    try {
      localStorage.removeItem('firebase:authUser');
      sessionStorage.clear();
    } catch (e) {
      console.warn('Failed to clear auth storage:', e);
    }

    // Redirect to login page
    window.location.href = '/login';
  }

  /**
   * Generic error handler that routes to appropriate handler
   */
  async handleError(error: Error): Promise<void> {
    try {
      if (this.isChunkError(error)) {
        await this.handleChunkError(error);
      } else if (this.isAuthError(error)) {
        await this.handleAuthError(error);
      } else {
        throw error; // Re-throw unknown errors
      }
    } catch (handlerError) {
      if (this.enableLogging) {
        console.error('Error in chunk error handler:', handlerError);
      }
      throw handlerError;
    }
  }

  /**
   * Reset retry count
   */
  reset(): void {
    this.retryCount = 0;
  }
}

// Create singleton instance
export const chunkErrorHandler = new ChunkErrorHandler({
  maxRetries: 3,
  retryDelay: 1000,
  enableLogging: true,
  onError: (error, retryCount) => {
    // Track chunk errors for analytics
    if ((window as any).gtag) {
      (window as any).gtag('event', 'chunk_load_error', {
        error_message: error.message,
        retry_count: retryCount,
        page_url: window.location.href
      });
    }
  },
  onMaxRetriesReached: (error) => {
    // Track when we have to force reload
    if ((window as any).gtag) {
      (window as any).gtag('event', 'chunk_load_force_reload', {
        error_message: error.message,
        page_url: window.location.href
      });
    }
  }
});

/**
 * Wrap dynamic imports with error handling
 */
export function withChunkErrorHandling<T>(
  importFn: () => Promise<T>
): () => Promise<T> {
  return async () => {
    try {
      return await importFn();
    } catch (error) {
      await chunkErrorHandler.handleError(error as Error);
      // If we get here, the error wasn't handled, so re-throw
      throw error;
    }
  };
}

/**
 * Setup global error handlers for chunk errors
 */
export function setupGlobalChunkErrorHandling(): void {
  // Handle unhandled promise rejections (common for dynamic imports)
  window.addEventListener('unhandledrejection', async (event) => {
    const error = event.reason;
    
    if (error instanceof Error) {
      try {
        await chunkErrorHandler.handleError(error);
        event.preventDefault(); // Prevent default error handling
      } catch (handlerError) {
        // Let the error bubble up if we can't handle it
        console.error('Unhandled chunk error:', handlerError);
      }
    }
  });

  // Handle general JavaScript errors
  window.addEventListener('error', async (event) => {
    const error = event.error;
    
    if (error instanceof Error) {
      try {
        await chunkErrorHandler.handleError(error);
        event.preventDefault(); // Prevent default error handling
      } catch (handlerError) {
        // Let the error bubble up if we can't handle it
        console.error('Unhandled script error:', handlerError);
      }
    }
  });

  // Handle resource loading errors (scripts, stylesheets, etc.)
  window.addEventListener('error', async (event) => {
    const target = event.target as HTMLElement;
    
    if (target && (target.tagName === 'SCRIPT' || target.tagName === 'LINK')) {
      const error = new Error(`Failed to load resource: ${(target as any).src || (target as any).href}`);
      
      try {
        await chunkErrorHandler.handleError(error);
        event.preventDefault();
      } catch (handlerError) {
        console.error('Unhandled resource error:', handlerError);
      }
    }
  }, true); // Use capture phase for resource errors
}

/**
 * Check if the current page needs a refresh due to new deployment
 */
export async function checkForUpdates(): Promise<boolean> {
  try {
    const response = await fetch('/version.json?' + Date.now(), {
      cache: 'no-cache'
    });
    
    if (response.ok) {
      const data = await response.json();
      const currentVersion = localStorage.getItem('app-version');
      
      if (currentVersion && currentVersion !== data.version) {
        console.log('New version detected:', data.version);
        return true;
      }
      
      localStorage.setItem('app-version', data.version);
    }
  } catch (error) {
    console.warn('Failed to check for updates:', error);
  }
  
  return false;
}

export default chunkErrorHandler;
